import type { Doc } from '@day';
import { createStore } from '../app/store';

/**
 * What the owner has just done and hasn't reached Firestore yet: a send waiting its 5 seconds, a route taken back, an
 * order changed, an alert cleared. The pages show it at once; Undo takes it away; once written, Firestore's own copy
 * takes over (its local cache answers straight away, so nothing jumps).
 */
export interface Pending {
  /** Route records by id: a new or changed record, or null for one going away. */
  routes: Map<string, Doc | null>;
  /** Stops sent one at a time, by id (null: going away). */
  sentStops: Map<string, Doc | null>;
  /** Alert ids cleared. */
  cleared: Set<string>;
  /** Hand-ins answered, by id. */
  handIns: Map<string, Doc>;
  /** Stops waiting for a route, by id: added or changed, or null for one leaving the list. */
  waiting: Map<string, Doc | null>;
  /** Zones by id: null for one being deleted (its 5 s with Undo). */
  zones: Map<string, Doc | null>;
}

const empty = (): Pending => ({ routes: new Map(), sentStops: new Map(), cleared: new Set(), handIns: new Map(), waiting: new Map(), zones: new Map() });

export const pending = createStore<Pending>(empty());

const withKey = <K, V>(m: Map<K, V>, k: K, v: V) => new Map(m).set(k, v);
const withoutKey = <K, V>(m: Map<K, V>, k: K) => {
  const n = new Map(m);
  n.delete(k);
  return n;
};

export const pendRoute = (id: string, doc: Doc | null) => pending.set((p) => ({ routes: withKey(p.routes, id, doc) }));
export const unpendRoute = (id: string) => pending.set((p) => ({ routes: withoutKey(p.routes, id) }));
/** Only while [doc] is still what's shown: a newer change to the same route stays shown until it's written too. */
export const unpendRouteIf = (id: string, doc: Doc | null) => pending.set((p) => (p.routes.get(id) === doc ? { routes: withoutKey(p.routes, id) } : {}));
export const pendSentStop = (id: string, doc: Doc | null) => pending.set((p) => ({ sentStops: withKey(p.sentStops, id, doc) }));
export const unpendSentStop = (id: string) => pending.set((p) => ({ sentStops: withoutKey(p.sentStops, id) }));
export const pendCleared = (id: string) => pending.set((p) => ({ cleared: new Set(p.cleared).add(id) }));
export const unpendCleared = (id: string) =>
  pending.set((p) => {
    const c = new Set(p.cleared);
    c.delete(id);
    return { cleared: c };
  });
export const pendHandIn = (id: string, doc: Doc) => pending.set((p) => ({ handIns: withKey(p.handIns, id, doc) }));
export const unpendHandIn = (id: string) => pending.set((p) => ({ handIns: withoutKey(p.handIns, id) }));

export const pendWaiting = (ids: string[], doc: (id: string) => Doc | null) =>
  pending.set((p) => {
    const w = new Map(p.waiting);
    for (const id of ids) w.set(id, doc(id));
    return { waiting: w };
  });
export const unpendWaiting = (ids: string[]) =>
  pending.set((p) => {
    const w = new Map(p.waiting);
    for (const id of ids) w.delete(id);
    return { waiting: w };
  });

export const pendZone = (id: string, doc: Doc | null) => pending.set((p) => ({ zones: withKey(p.zones, id, doc) }));
export const unpendZone = (id: string) => pending.set((p) => ({ zones: withoutKey(p.zones, id) }));

/** Signing out: nothing of one business's stays for the next account on this computer. */
export const resetPending = () => pending.set(empty());

/** Any records with what's pending laid over them (null: going away). */
export function overlayMap(base: Map<string, Doc>, over: Map<string, Doc | null>): Map<string, Doc> {
  if (!over.size) return base;
  const out = new Map(base);
  for (const [id, doc] of over) {
    if (doc) out.set(id, doc);
    else out.delete(id);
  }
  return out;
}

/** Waiting stops with what's pending laid over them. */
export function overlayWaiting(waiting: Map<string, Doc>, p: Pending): Map<string, Doc> {
  if (!p.waiting.size) return waiting;
  const out = new Map(waiting);
  for (const [id, doc] of p.waiting) {
    if (doc) out.set(id, doc);
    else out.delete(id);
  }
  return out;
}

/** Route records with what's pending laid over them. */
export function overlay(routes: Map<string, Doc>, p: Pending): Map<string, Doc> {
  if (!p.routes.size) return routes;
  const out = new Map(routes);
  for (const [id, doc] of p.routes) {
    if (doc) out.set(id, doc);
    else out.delete(id);
  }
  return out;
}
